import Phaser from 'phaser';
import { controlsRegion } from '../../game/state.js';
import type {
  GameState,
  MapDefinition,
  Player,
  RegionId,
} from '../../game/types.js';
import { MAP_Y_OFFSET } from '../PhaserGame.js';
import {
  TEAM_COLORS_INT,
  TERRITORY_SIZE as DEFAULT_TERRITORY_SIZE,
} from '../style.js';

// Stroke geometry. The outline traces the actual outer edge of the
// region's tiles — the polygon sits ON the tile borders, and the
// stroke (5px) straddles them: ~2.5px inside the tiles, ~2.5px
// outside. No padding inflation means the corners stay clean (no
// spurs from overlapping segment extensions).
const RING_STROKE_WIDTH = 5;
const RING_ALPHA = 0.95;

// Depth: above the hex grid + adjacency lines (both default 0) but below
// selected territories (depth 100). The ring frames the region without
// fighting with selection rings or unit labels.
const RING_DEPTH = 6;

interface Vec2 {
  readonly x: number;
  readonly y: number;
}

/**
 * Renders a colored ring tracing the actual outer perimeter of any
 * region that's fully controlled by a single team. The polygon walks
 * the tile-flush boundary clockwise and is drawn with Phaser's
 * `strokePoints` — Phaser's line-join handles convex and concave
 * corners cleanly without the visual spurs the prior segment-by-
 * segment renderer produced.
 *
 * Works for any tile-flush shape on any map: rectangles (Ring),
 * L-shapes (Slash's Northwilds), plus-shapes (Terra's Euro), 3×3
 * minus a corner (Asia), T-shapes (SA + Africa).
 *
 * Color comes from the team palette in team play, or the controlling
 * player's color in pure FFA.
 */
export class RegionOverlay {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private readonly polygonsByRegion: ReadonlyMap<RegionId, readonly Vec2[][]>;

  constructor(scene: Phaser.Scene, map: MapDefinition) {
    this.graphics = scene.add.graphics();
    this.graphics.setDepth(RING_DEPTH);
    // Apply the map offset to the whole graphics object — every
    // polygon stroked on it gets shifted automatically without
    // touching computeRegionPolygons, which still works in the
    // map data's natural coordinates.
    this.graphics.setY(MAP_Y_OFFSET);
    this.polygonsByRegion = computeRegionPolygons(map);
  }

  /**
   * Repaint based on current ownership. Called from `refreshAll` after
   * any state change. Cheap — clears the Graphics and re-strokes each
   * controlled region's pre-computed polygon.
   *
   * Ring color always comes from the team palette (Aqua / Solar / Rose
   * / Kelp), keyed off the controlling team number. In team play the
   * palette index is the team. In FFA each seat is its own team, so
   * each player still gets a distinct palette color — and crucially,
   * one that doesn't match its own tile color. Painting the ring in
   * the seat color (the prior FFA branch) made it camouflage against
   * its own tiles and disappear; the palette pops in every mode.
   */
  refresh(state: GameState, map: MapDefinition): void {
    this.graphics.clear();

    for (const region of map.regions) {
      const controller = findController(state, map, region.id);
      if (!controller) continue;
      const polygons = this.polygonsByRegion.get(region.id);
      if (!polygons || polygons.length === 0) continue;

      const colorInt =
        TEAM_COLORS_INT[(controller.team - 1) % TEAM_COLORS_INT.length] ??
        TEAM_COLORS_INT[0]!;

      this.graphics.lineStyle(RING_STROKE_WIDTH, colorInt, RING_ALPHA);
      for (const polygon of polygons) {
        // strokePoints with closeShape=true draws the polyline AND
        // closes back to the first vertex, giving a clean closed
        // outline with miter joins at every corner.
        this.graphics.strokePoints(polygon as Vec2[], true);
      }
    }
  }
}

/**
 * Find the team that controls every tile in the region, plus a
 * representative player on that team. Uses the team-aware
 * `controlsRegion` helper so a region counts as controlled when ANY
 * teammate's `controlsRegion` returns true (in team play, all
 * teammates share the same answer).
 */
function findController(
  state: GameState,
  map: MapDefinition,
  regionId: RegionId
): { player: Player; team: number } | null {
  for (const player of state.players) {
    if (player.eliminated) continue;
    if (controlsRegion(state, map, regionId, player.id)) {
      return { player, team: player.team };
    }
  }
  return null;
}

/**
 * For each region, compute a list of closed polygons that trace its
 * outer perimeter. Done once at construction; per-frame refreshes
 * are pure stroke calls.
 *
 * Algorithm:
 *  1. Build oriented edges going clockwise. Each tile contributes
 *     the sides whose grid neighbor isn't a same-region tile.
 *     Top edge runs left→right; right edge top→bottom; bottom edge
 *     right→left; left edge bottom→top. Orienting them makes the
 *     stitch step trivial.
 *  2. Build a start-point → edge map.
 *  3. Walk: from any unused edge, follow the chain by looking up
 *     the next edge whose start matches the current edge's end.
 *     One pass produces one closed polygon. Repeat for any edges
 *     not yet visited (handles disconnected regions or holes).
 */
function computeRegionPolygons(
  map: MapDefinition
): ReadonlyMap<RegionId, readonly Vec2[][]> {
  const tileSize = map.tileSize ?? DEFAULT_TERRITORY_SIZE;
  const half = tileSize / 2;

  const tilesByRegion = new Map<RegionId, Set<string>>();
  for (const t of map.territories) {
    const key = `${t.position.x},${t.position.y}`;
    let set = tilesByRegion.get(t.region);
    if (!set) {
      set = new Set();
      tilesByRegion.set(t.region, set);
    }
    set.add(key);
  }

  const out = new Map<RegionId, readonly Vec2[][]>();
  for (const region of map.regions) {
    const tiles = tilesByRegion.get(region.id);
    if (!tiles) continue;

    interface Edge {
      readonly start: Vec2;
      readonly end: Vec2;
    }
    const edges: Edge[] = [];
    for (const key of tiles) {
      const [xStr, yStr] = key.split(',');
      const x = Number(xStr);
      const y = Number(yStr);

      // Top edge, going left→right (NW corner to NE corner).
      if (!tiles.has(`${x},${y - tileSize}`)) {
        edges.push({
          start: { x: x - half, y: y - half },
          end: { x: x + half, y: y - half },
        });
      }
      // Right edge, going top→bottom (NE corner to SE corner).
      if (!tiles.has(`${x + tileSize},${y}`)) {
        edges.push({
          start: { x: x + half, y: y - half },
          end: { x: x + half, y: y + half },
        });
      }
      // Bottom edge, going right→left (SE corner to SW corner).
      if (!tiles.has(`${x},${y + tileSize}`)) {
        edges.push({
          start: { x: x + half, y: y + half },
          end: { x: x - half, y: y + half },
        });
      }
      // Left edge, going bottom→top (SW corner to NW corner).
      if (!tiles.has(`${x - tileSize},${y}`)) {
        edges.push({
          start: { x: x - half, y: y + half },
          end: { x: x - half, y: y - half },
        });
      }
    }
    if (edges.length === 0) continue;

    // Build start-point → edge index. Each start point belongs to
    // exactly one edge for a manifold tile-flush region.
    const startMap = new Map<string, Edge>();
    for (const e of edges) {
      startMap.set(`${e.start.x},${e.start.y}`, e);
    }

    // Walk each connected component into its own closed polygon.
    const used = new Set<Edge>();
    const polygons: Vec2[][] = [];
    for (const startEdge of edges) {
      if (used.has(startEdge)) continue;
      const polygon: Vec2[] = [];
      let current: Edge | undefined = startEdge;
      const firstKey = `${startEdge.start.x},${startEdge.start.y}`;
      while (current && !used.has(current)) {
        used.add(current);
        polygon.push(current.start);
        const nextKey = `${current.end.x},${current.end.y}`;
        if (nextKey === firstKey) {
          // Loop closed.
          break;
        }
        current = startMap.get(nextKey);
      }
      if (polygon.length >= 3) polygons.push(polygon);
    }

    out.set(region.id, polygons);
  }
  return out;
}
